import { Grid } from "@mui/material";
import { useEvents } from "api/events";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";

interface Props {
  activeRule: number;
  event?: string;
  isFullWidth?: boolean;
  setFieldValue: (field: string, value?: string | []) => void;
  error: any;
}

export const CustomEvent = ({
  activeRule,
  event,
  isFullWidth,
  setFieldValue,
  error,
}: Props) => {
  const { message } = useMessage();
  const [customEventsSearch, setCustomEventsSearch] = useState("");

  const { useInfiniteCustomEventsSchemaList, useCustomEventSchemaDetails } =
    useEvents();

  const {
    data: infiniteCustomEventSchemaList,
    size: customEventSchemaSize,
    setSize: setCustomEventSchemaSize,
    isValidating,
  } = useInfiniteCustomEventsSchemaList({
    filters: customEventsSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: customEventsSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    rows: 50,
    sortKey: "name",
  });

  const {
    infiniteData: customEventSchemaList,
    anyElementsToFetch: anyCustomEventSchemaToFetch,
  } = useManageInfiniteScroll(infiniteCustomEventSchemaList || []);

  const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(event);
  const isSchemaAllowed = ({
    schema,
  }: {
    schema: { fields: { type: string }[] };
  }) => {
    const allowedFieldTypes = ["boolean", "datetime", "number", "text"];
    return schema?.fields?.every(({ type }: { type: string }) =>
      allowedFieldTypes.includes(type),
    );
  };

  const allowedSchemas = customEventSchemaList.filter(isSchemaAllowed) || [];

  const initialValue = selectedSchemaDetails && {
    id: selectedSchemaDetails.eventType as string,
    label: selectedSchemaDetails.name,
  };

  return (
    <>
      {!isFullWidth && <Grid xs={6} item />}
      <Grid item xs={isFullWidth ? 12 : 6}>
        <SelectInput
          displaySearch
          data-test-id={`rule-${activeRule}-trigger`}
          width="100%"
          items={
            allowedSchemas.map(
              (event: { eventType: string; name: string }) => ({
                label: event.name,
                id: event.eventType,
              }),
            ) || []
          }
          label={<RequiredFieldLabel text={message({ id: "eventType" })} />}
          name={`rules[${activeRule}].event`}
          placeholder={message({
            id: "typeInToSearch",
          })}
          asyncSearch={(searchedQuery: string) =>
            setCustomEventsSearch(searchedQuery)
          }
          initialValue={initialValue}
          onChange={() => {
            // Clean field connected to chosen event type
            setFieldValue(`rules[${activeRule}].conditions`, []);
            setFieldValue(`rules[${activeRule}].aggregation.rule`, undefined);
          }}
          anyElementsToFetch={anyCustomEventSchemaToFetch}
          loadMoreElements={() =>
            !isValidating && setCustomEventSchemaSize(customEventSchemaSize + 1)
          }
          error={error}
        />
      </Grid>
    </>
  );
};
